"use client"

import React, { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { 
  BarChart3, 
  TrendingUp, 
  DollarSign, 
  ShoppingBag, 
  Users, 
  Clock,
  Calendar,
  Download,
  Filter,
  RefreshCw,
  PieChart,
  Activity,
  Target,
  Award
} from "lucide-react"
import { restaurantPortalAPI } from "../../services/restaurantApiClient"
import { useRestaurantAuth } from "../../contexts/RestaurantAuthContext"
import { useLanguage } from "../../contexts/LanguageContext"
import PageHeader from "../../components/ui/dashboard/PageHeader"
import StatCard from "../../components/ui/dashboard/StatCard"
import DataCard from "../../components/ui/dashboard/DataCard"
import LoadingSpinner from "../../components/ui/dashboard/LoadingSpinner"
import { Button } from "../../components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import { Badge } from "../../components/ui/badge"
import { Progress } from "../../components/ui/progress"

interface AnalyticsData {
  period: string
  dateRange: {
    start: string
    end: string
  }
  overview: {
    totalRevenue: number
    totalOrders: number
    averageOrderValue: number
    uniqueCustomers: number
  }
  breakdowns: {
    status: Record<string, number>
    paymentMethod: Record<string, number>
    orderType: Record<string, number>
  }
  topSellingItems: Array<{
    name: string
    quantity: number
    revenue: number
    category: string
  }>
  revenueTrend: Array<{
    date: string
    revenue: number
  }>
}

interface RealtimeStats {
  today: {
    orders: number
    revenue: number
  }
  current: {
    pendingOrders: number
    hourlyOrders: number
    hourlyRevenue: number
  }
  performance: {
    avgPreparationTime: number
    completionRate: number
  }
}

const RestaurantAnalyticsPage: React.FC = () => {
  const { restaurantId } = useParams()
  const { user } = useRestaurantAuth()
  const { t, language, dir } = useLanguage()
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null)
  const [realtimeStats, setRealtimeStats] = useState<RealtimeStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedPeriod, setSelectedPeriod] = useState("30d")
  const [selectedReportType, setSelectedReportType] = useState("monthly")

  const fetchAnalytics = async () => {
    try {
      setLoading(true)
      const [analyticsResponse, realtimeResponse] = await Promise.all([
        restaurantPortalAPI.getAnalytics(restaurantId!, selectedPeriod),
        restaurantPortalAPI.getRealtimeStats(restaurantId!)
      ])
      
      setAnalyticsData(analyticsResponse.data)
      setRealtimeStats(realtimeResponse.data)
    } catch (error) {
      console.error("Error fetching analytics:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (restaurantId) {
      fetchAnalytics()
    }
  }, [restaurantId, selectedPeriod])

  const downloadReport = async () => {
    try {
      const response = await restaurantPortalAPI.getReports(restaurantId!, selectedReportType)
      const report = response.data
      
      // Create CSV content
      const csvContent = generateCSVReport(report)
      
      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `restaurant-report-${selectedReportType}-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error("Error downloading report:", error)
    }
  }

  const generateCSVReport = (report: any) => {
    const headers = [
      "Report Type",
      "Period Start",
      "Period End",
      "Total Revenue",
      "Total Orders",
      "Completed Orders",
      "Cancelled Orders",
      "Average Order Value",
      "Unique Customers"
    ]
    
    const row = [
      report.type,
      new Date(report.period.start).toLocaleDateString(),
      new Date(report.period.end).toLocaleDateString(),
      report.summary.totalRevenue,
      report.summary.totalOrders,
      report.summary.completedOrders,
      report.summary.cancelledOrders,
      report.summary.averageOrderValue,
      report.summary.uniqueCustomers
    ]
    
    return [headers.join(','), row.join(',')].join('\n')
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'SDG'
    }).format(amount)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
      case 'served':
      case 'picked-up':
        return 'bg-green-100 text-green-800'
      case 'preparing':
      case 'ready':
        return 'bg-blue-100 text-blue-800'
      case 'placed':
      case 'confirmed':
        return 'bg-yellow-100 text-yellow-800'
      case 'cancelled':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return <LoadingSpinner size="3" text={t("loadingAnalytics")} />
  }

  return (
    <div className="space-y-6" dir={dir}>
      <PageHeader
        title={t("analyticsAndReports")}
        subtitle={t("trackRestaurantPerformance")}
      >
        <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">{t("last7Days")}</SelectItem>
              <SelectItem value="30d">{t("last30Days")}</SelectItem>
              <SelectItem value="90d">{t("last90Days")}</SelectItem>
              <SelectItem value="1y">{t("lastYear")}</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchAnalytics} variant="outline" size="sm">
            <RefreshCw className={`w-4 h-4 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
            {t("refresh")}
          </Button>
        </div>
      </PageHeader>

      {/* Real-time Stats */}
      {realtimeStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title={t("todaysOrders")}
            value={realtimeStats.today.orders}
            icon={ShoppingBag}
            color="bg-blue-500"
            subtitle={`${t("revenue")}: ${formatCurrency(realtimeStats.today.revenue)}`}
          />
          <StatCard
            title={t("pendingOrders")}
            value={realtimeStats.current.pendingOrders}
            icon={Clock}
            color="bg-orange-500"
            subtitle={`${t("thisHour")}: ${realtimeStats.current.hourlyOrders}`}
          />
          <StatCard
            title={t("avgPrepTime")}
            value={`${realtimeStats.performance.avgPreparationTime} ${t("min")}`}
            icon={Activity}
            color="bg-green-500"
            subtitle={`${t("completionRate")}: ${realtimeStats.performance.completionRate.toFixed(1)}%`}
          />
          <StatCard
            title={t("hourlyRevenue")}
            value={formatCurrency(realtimeStats.current.hourlyRevenue)}
            icon={TrendingUp}
            color="bg-red-500"
            subtitle={t("lastHourPerformance")}
          />
        </div>
      )}

      {/* Analytics Overview */}
      {analyticsData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title={t("totalRevenue")}
              value={formatCurrency(analyticsData.overview.totalRevenue)}
              icon={DollarSign}
              color="bg-green-500"
              subtitle={`${analyticsData.overview.totalOrders} ${t("orders")}`}
            />
            <StatCard
              title={t("averageOrderValue")}
              value={formatCurrency(analyticsData.overview.averageOrderValue)}
              icon={Target}
              color="bg-blue-500"
              subtitle={t("perOrderAverage")}
            />
            <StatCard
              title={t("uniqueCustomers")}
              value={analyticsData.overview.uniqueCustomers}
              icon={Users}
              color="bg-purple-500"
              subtitle={t("repeatCustomers")}
            />
            <StatCard
              title={t("period")}
              value={selectedPeriod}
              icon={Calendar}
              color="bg-orange-500"
              subtitle={`${new Date(analyticsData.dateRange.start).toLocaleDateString()} - ${new Date(analyticsData.dateRange.end).toLocaleDateString()}`}
            />
          </div>

          {/* Order Status Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DataCard
              title={t("orderStatusBreakdown")}
              subtitle={t("distributionOfOrdersByStatus")}
            >
              <div className="space-y-4">
                {Object.entries(analyticsData.breakdowns.status).map(([status, count]) => (
                  <div key={status} className={`flex items-center justify-between`}>
                    <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                      <Badge className={getStatusColor(status)}>
                        {status}
                      </Badge>
                      <span className="text-sm text-gray-600 dark:text-gray-400">{count} {t("orders")}</span>
                    </div>
                    <div className="w-24">
                      <Progress 
                        value={(count / analyticsData.overview.totalOrders) * 100} 
                        className="h-2"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </DataCard>

            {/* Payment Method Breakdown */}
            <DataCard
              title={t("paymentMethods")}
              subtitle={t("ordersByPaymentMethod")}
            >
              <div className="space-y-4">
                {Object.entries(analyticsData.breakdowns.paymentMethod).map(([method, count]) => (
                  <div key={method} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Badge variant="outline">
                        {method}
                      </Badge>
                      <span className="text-sm text-gray-600 dark:text-gray-400">{count} {t("orders")}</span>
                    </div>
                    <div className="w-24">
                      <Progress 
                        value={(count / analyticsData.overview.totalOrders) * 100} 
                        className="h-2"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </DataCard>
          </div>

          {/* Top Selling Items */}
          <DataCard
            title={t("topSellingItems")}
            subtitle={t("mostPopularFoodItems")}
          >
            <div className="space-y-4">
              {analyticsData.topSellingItems.slice(0, 10).map((item, index) => (
                <div key={item.name} className="flex items-center justify-between p-4 border rounded-lg dark:border-gray-700">
                  <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
                    <div className="flex items-center justify-center w-8 h-8 bg-red-600 text-white rounded-full text-sm font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{item.name}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{item.category}</p>
                    </div>
                  </div>
                  <div className={`${language === 'ar' ? 'text-left' : 'text-right'}`}>
                    <p className="font-medium text-gray-900 dark:text-white">{item.quantity} {t("sold")}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{formatCurrency(item.revenue)}</p>
                  </div>
                </div>
              ))}
            </div>
          </DataCard>

          {/* Revenue Trend */}
          <DataCard
            title={t("revenueTrend")}
            subtitle={t("dailyRevenueOverPeriod")}
          >
            <div className="space-y-4">
              {analyticsData.revenueTrend.map((day) => (
                <div key={day.date} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{new Date(day.date).toLocaleDateString()}</span>
                  <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(day.revenue)}</span>
                </div>
              ))}
            </div>
          </DataCard>
        </>
      )}

      {/* Reports Section */}
      <DataCard
        title={t("generateReports")}
        subtitle={t("downloadDetailedReports")}
      >
        <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
          <Select value={selectedReportType} onValueChange={setSelectedReportType}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">{t("daily")}</SelectItem>
              <SelectItem value="weekly">{t("weekly")}</SelectItem>
              <SelectItem value="monthly">{t("monthly")}</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={downloadReport} className={`flex items-center ${language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
            <Download className="w-4 h-4" />
            <span>{t("downloadReport")}</span>
          </Button>
        </div>
      </DataCard>
    </div>
  )
}

export default RestaurantAnalyticsPage 